import { useLocation, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { Button, Form, Input, Typography } from "antd";
import {
  StyledSign,
  StyledSignContent,
  StyledSignForm,
  SignInButton,
} from "../index.styled";
import AppPageMeta from "@lib/components/AppComponents/AppPageMeta";
import { useTypedDispatch } from "@lib/redux";
import { useCallback, useEffect } from "react";
import AppLoader from "@lib/components/AppComponents/AppLoader";
import IntlMessages from "@lib/helpers/IntlMessages";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { TbArrowBackUp } from "react-icons/tb";
import { createYupSync } from "@lib/helpers/TableHelper";
import { twoFactorSchema as yupSchema } from "@lib/schemas/auth";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { publicFallback } from "@app/.core/AppRoutes";
import { apiAuthSignin } from "@lib/api/auth";
import { setAuth } from "@lib/redux/slices/auth";
import { AxiosError, AxiosResponse } from "axios";
import { AuthUser } from "@lib/types";
import { AppLayout } from "@lib/components/AppComponents";
import { ROUTES } from "@lib/constants";
import { NavStyle } from "@lib/constants/AppEnums";

const TwoFactor = () => {
  const { messages } = useIntl();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const [form] = Form.useForm();
  const code = Form.useWatch("twoFactorAuthenticationCode", form);

  const signInMutation = useMutation<
    AxiosResponse<AuthUser>,
    AxiosError<any>,
    { username: string; password: string; twoFactorAuthenticationCode?: string }
  >({
    mutationFn: apiAuthSignin,
    onSuccess: ({ data }) => {
      dispatch(setAuth(data));
      toast.dismiss();
      navigate(publicFallback);
    },
  });

  const { mutate, isPending } = signInMutation;

  const handleSubmit = useCallback(
    (code: string) => {
      mutate({
        username: state?.values?.email,
        password: state?.values?.password,
        twoFactorAuthenticationCode: code,
      });
    },
    [code],
  );

  useEffect(() => {
    if (code && code.length == 6) {
      handleSubmit(code);
    }
  }, [code]);

  useEffect(() => {
    if (!state?.values?.email || !state?.values?.password) {
      navigate(ROUTES.AUTH.LOGIN);
    }
  }, [state]);

  return (
    <AppLayout overrideLayout={NavStyle.NON_AUTH}>
      <AppPageMeta title={messages["common.signIn"] as string} />

      {isPending && <AppLoader />}
      <StyledSign>
        <StyledSignContent>
          <StyledSignForm
            name="two-factor-form"
            form={form}
            onFinish={(values: any) => {
              handleSubmit(values.twoFactorAuthenticationCode);
            }}
            initialValues={{
              twoFactorAuthenticationCode: "",
            }}
          >
            <Form.Item
              name="twoFactorAuthenticationCode"
              className="form-field"
              rules={[createYupSync({ schema: yupSchema })]}
              style={{ marginTop: "50px" }}
            >
              <Typography.Title level={5}>Two Factor Code</Typography.Title>
              <Input.OTP
                autoFocus
                disabled={isPending}
                onChange={(e) => {
                  const value = e.replace(/\D/g, "").slice(0, 6);
                  form.setFieldValue("twoFactorAuthenticationCode", value);
                }}
              />
            </Form.Item>

            <StyledFlex style={{ justifyContent: "flex-end", gap: "25px" }}>
              <Button
                icon={<TbArrowBackUp />}
                onClick={() => navigate(ROUTES.AUTH.LOGIN)}
              >
                <IntlMessages id="common.goBack" />
              </Button>
              <SignInButton
                type="primary"
                htmlType="submit"
                loading={isPending}
              >
                <IntlMessages id="common.verify" />
              </SignInButton>
            </StyledFlex>
          </StyledSignForm>
        </StyledSignContent>
      </StyledSign>
    </AppLayout>
  );
};

export default TwoFactor;
